/**
 * Household Card Component
 *
 * A card-based display for household information, designed for mobile-friendly staff viewing.
 * Shows parent name, contact info, emergency contact, and status.
 */

import {
  Badge,
  Box,
  Card,
  Flex,
  HStack,
  Icon,
  Text,
  VStack,
} from "@chakra-ui/react"
import { FiAlertTriangle, FiHome, FiMail, FiPhone } from "react-icons/fi"

import { HouseholdActionsMenu } from "@/components/layout/Common/HouseholdActionsMenu"
import type { HouseholdResponse } from "@/types/household"

export interface HouseholdCardProps {
  household: HouseholdResponse
  /** Whether to show action menu */
  showActions?: boolean
  /** Click handler for the card */
  onClick?: () => void
}

export function HouseholdCard({
  household,
  showActions = true,
  onClick,
}: HouseholdCardProps) {
  const isActive = household.is_active !== false

  return (
    <Card.Root
      bg="white"
      borderRadius="xl"
      boxShadow="sm"
      borderWidth="1px"
      borderColor="gray.100"
      _hover={{
        boxShadow: "md",
        borderColor: "ui.main",
        transform: onClick ? "translateY(-2px)" : undefined,
      }}
      transition="all 0.2s"
      cursor={onClick ? "pointer" : "default"}
      onClick={onClick}
    >
      <Card.Body p={5}>
        <VStack align="stretch" gap={4}>
          {/* Header with parent name and status */}
          <Flex justify="space-between" align="start">
            <Flex align="center" gap={3}>
              <Box
                w="48px"
                h="48px"
                borderRadius="full"
                bg="ui.backgroundAlt"
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
              >
                <Icon color="ui.main" fontSize="xl">
                  <FiHome />
                </Icon>
              </Box>
              <Box>
                <Text fontWeight="semibold" fontSize="lg" color="gray.800">
                  {household.parent_first_name} {household.parent_last_name}
                </Text>
                <Badge
                  colorPalette={isActive ? "green" : "gray"}
                  variant="subtle"
                  size="sm"
                >
                  {isActive ? "Active" : "Inactive"}
                </Badge>
              </Box>
            </Flex>

            {/* Actions menu */}
            {showActions && (
              <Box onClick={(e) => e.stopPropagation()}>
                <HouseholdActionsMenu household={household} />
              </Box>
            )}
          </Flex>

          {/* Contact info */}
          <VStack align="stretch" gap={2}>
            <HStack fontSize="sm" color="gray.600">
              <Icon color="gray.400">
                <FiMail />
              </Icon>
              <Text>{household.email || "N/A"}</Text>
            </HStack>

            {household.phone && (
              <HStack fontSize="sm" color="gray.600">
                <Icon color="gray.400">
                  <FiPhone />
                </Icon>
                <Text>{household.phone}</Text>
              </HStack>
            )}
          </VStack>

          {/* Emergency contact */}
          {household.emergency_contact_name && (
            <Box
              bg="orange.50"
              borderRadius="md"
              p={3}
              borderLeftWidth="3px"
              borderLeftColor="orange.400"
            >
              <HStack color="orange.600" fontSize="sm">
                <Icon>
                  <FiAlertTriangle />
                </Icon>
                <Text fontWeight="medium">Emergency Contact</Text>
              </HStack>
              <Text fontSize="sm" color="gray.700" mt={1}>
                {household.emergency_contact_name}
                {household.emergency_contact_phone &&
                  ` - ${household.emergency_contact_phone}`}
              </Text>
            </Box>
          )}

          {/* Address preview */}
          {household.address && (
            <Text fontSize="sm" color="gray.500" lineClamp={1}>
              {household.address}
            </Text>
          )}
        </VStack>
      </Card.Body>
    </Card.Root>
  )
}

export default HouseholdCard
